/**
 * VaultProvider — the shared data layer for the routed Document Vault.
 *
 * It runs the M1-backed hooks ONCE (the global-attribute catalog and spaces),
 * computes the server→presentational lookup maps, and exposes everything via
 * `useVaultData()`. Each route reads what it needs from context and adds only its
 * own route-specific query (e.g. the server-side documents grid in
 * `DocumentsRoute`, `useVaultDocument(id)` in `DocumentDetailRoute`).
 *
 * NOTE: there is no shared page of documents. Every surface that reads documents
 * either fetches its own (the Documents grid, the detail overlay) or is switched
 * off (Sharing, the Space-local attributes tab) — see `documents` below.
 *
 * This replaces the old load-once-optimistic model of `DocumentVaultView`: the
 * backed mutations (`saveSpace`, `addGlobalAttr`, `saveGlobalAttr`) already
 * `refetchQueries`, so context data updates reactively after a write — no local
 * snapshot to go stale.
 *
 * Not-yet-backed surfaces (Sharing, review queue, upload bytes, reference lists,
 * channels, access) stay presentational: their data comes in as props here and is
 * passed straight through the context, empty by default, exactly as before.
 */
import React, { createContext, useCallback, useContext, useMemo } from "react";
import { useFeatureFlag } from "@jsq/flags-react";
import {
  useGlobalAttributeAdmin,
  type UseGlobalAttributeAdminReturn,
} from "#common/graphql/Attribute";
import { optionValueByIdFromCatalog } from "#common/graphql/Attribute/mappers";
import { useVaultSpaces } from "#common/graphql/Space";
import { useTransientToast, VaultToast, type ToastTone } from "./VaultToast";
import type {
  AccessGrant,
  Channel,
  CurrentUser,
  FieldDef,
  GlobalAttribute,
  QuarantineItem,
  ReferenceData,
  ShareLogEntry,
  Space,
  VaultCapability,
  VaultDocument,
  VaultDomain,
} from "#common/types/vault";

/**
 * DV-693 §5's recommended mechanism: gate on a capability list carried by
 * `user`, off by default so merging this ahead of the backend field that
 * populates it (§5's filed follow-up) changes nothing. Flag OFF ⇒ every
 * capability check passes — the pre-DV-354 behaviour, matching every other
 * `can_*` string's absence today. Flag ON with no `user.capabilities` ⇒ every
 * check fails (fail closed), never "grant everything".
 */
const DOCUMENT_VAULT_GRANULAR_AUTH_FLAG = "documentVaultGranularAuth";

/** M1 domains (Fund Admin + Fay are the only live domains). */
export const DEFAULT_DOMAINS: VaultDomain[] = [
  { id: "fundadmin", name: "Fund Admin", short: "FA", role: "Core fund administration" },
  { id: "fae", name: "Fay", short: "FY", role: "Oversight & QA" },
];

export const EMPTY_REFERENCE: ReferenceData = {
  clients: [],
  entities: {},
  investors: [],
  periods: [],
  fundAdmins: [],
  assetClasses: [],
  roles: [],
};

/**
 * No shared page of documents is fetched. It used to be one `first: 50`
 * `vaultDocumentsSearch` call feeding three consumers, and none of them wanted it:
 *
 *   - Sharing — switched off (see `SHARING_ENABLED` in `VaultLayout`; hardcoded,
 *     not flag-driven — there is no backend behind it yet).
 *   - The Space-local attributes tab — switched off (`useFeatureFlag("documentVaultM2")`
 *     in `DocumentTypeSettings`, resolved via `@jsq/flags-react`).
 *   - The attribute-removal impact count in the Space editor — a count taken from
 *     the first 50 documents in the workspace was never trustworthy anyway, so
 *     that modal now omits the sentence rather than quoting a number it can't
 *     stand behind (see `SpaceEditor`).
 *
 * The Documents grid always fetched its own cursor pages, and the detail overlay
 * fetches by id, so neither is affected. Restoring the page means re-adding the
 * `useVaultDocumentsSearch` call here and mapping `nodes` through `lookups`.
 */
const NO_SHARED_DOCUMENTS: VaultDocument[] = [];
// Frozen because every provider hands out this same instance: a consumer that
// `push`ed into it would otherwise be mutating what every other provider serves.
// Frozen in place rather than via `Object.freeze(...)`'s return value, which is
// `Readonly<T>` and would need a cast to stay assignable to the declared type.
Object.freeze(NO_SHARED_DOCUMENTS);

/** Server→presentational lookups needed to map documents onto the vault shape. */
export type VaultLookups = {
  /** space id → vault domain id */
  domainBySpaceId: Record<string, string>;
  /** backend attributeId → vault global `key` */
  globalKeyByAttributeId: Record<string, string>;
  /** backend attributeId → space-local attribute name */
  localNameByAttributeId: Record<string, string>;
  /**
   * AttributeValueOption id → display value (select labels on the read path).
   * Optional so existing consumer-built `VaultLookups` stay type-compatible;
   * `apiVaultDocumentToVaultDocument` defaults a missing map to `{}`.
   */
  optionValueById?: Record<string, string>;
};

/** The shape exposed by `useVaultData()`. */
export type VaultData = {
  user: CurrentUser;
  loading: boolean;
  saving: boolean;

  /* M1-backed, fetched once here */
  spaces: Space[];
  globalLib: GlobalAttribute[];
  globalIdByKey: Record<string, string>;
  domains: VaultDomain[];
  domainOrder?: string[];
  /**
   * @deprecated ALWAYS EMPTY, and removed in the next major — no shared documents
   * page is fetched any more (see `NO_SHARED_DOCUMENTS` for why, and what to do to
   * bring it back). Do not read it: it reports "no documents" rather than "not
   * loaded", so a consumer branching on `documents.length` renders an empty state
   * for a workspace full of documents.
   *
   * Fetch what you need instead — `useVaultDocumentsSearch` for a page,
   * `useVaultDocument(id)` for one — the way `DocumentsRoute` and
   * `DocumentDetailRoute` do. Kept for one major so the surfaces that take a
   * `documents` prop keep compiling; deleting it now would be a breaking change
   * (AGENTS.md, Deprecation).
   */
  documents: VaultDocument[];
  lookups: VaultLookups;
  /**
   * What to render in place of route content while `loading`. Held here rather
   * than swapped in by the provider itself: the provider sits ABOVE the chrome,
   * so gating here took the sidebar down with it. `VaultLayout` renders this
   * inside `<main>` instead, leaving the nav on screen and interactive.
   */
  loadingFallback?: React.ReactNode;

  /* Presentational passthrough (not-yet-backed surfaces) */
  quarantine: QuarantineItem[];
  channels: Channel[];
  reference: ReferenceData;
  fields: Record<string, FieldDef>;
  access: Record<string, AccessGrant[]>;
  shareLog: ShareLogEntry[];
  clients: string[];

  /* M1-backed mutations (performed inside the library) */
  saveSpace: (space: Space, id: string | null) => Promise<Space | null>;
  addGlobalAttr: (attr: GlobalAttribute) => Promise<void>;
  saveGlobalAttr: (key: string, patch: Partial<GlobalAttribute>) => Promise<void>;
  /**
   * Promote a space-local attribute into the global library (DV-648). Unlike
   * `refetch`, the Promise is kept: the promotion modal awaits it and stays open
   * if it rejects.
   */
  promoteLocal: (localId: string, newLabel?: string) => Promise<void>;
  /**
   * Edit a space-local attribute in place (DV-674). `saved` is the attribute as
   * the editor found it, which is what makes the option patch id-stable. Like
   * `promoteLocal`, the Promise is kept: the edit modal awaits it and stays open
   * if it rejects.
   */
  saveLocal: UseGlobalAttributeAdminReturn["saveLocal"];
  refetch: () => void;
  showToast: (message: string, tone?: ToastTone) => void;
  /**
   * Whether `user` holds `capability`, per `documentVaultGranularAuth`
   * (DV-693 §5). Flag off (the default until every environment's holding on
   * DV-699) always returns true — see the flag's own doc comment.
   */
  hasCapability: (capability: VaultCapability) => boolean;
};

const VaultDataContext = createContext<VaultData | null>(null);

/** Read the shared Document Vault data. Throws outside a `VaultProvider`. */
export const useVaultData = (): VaultData => {
  const ctx = useContext(VaultDataContext);
  if (!ctx) throw new Error("useVaultData must be used within a <VaultProvider>.");
  return ctx;
};

export type VaultProviderProps = {
  /** The signed-in user (from the app's auth context — not a DV API call). */
  user: CurrentUser;
  /** Domains catalog (display + space→domain mapping). Defaults to the M1 set. */
  domains?: VaultDomain[];
  domainOrder?: string[];
  /** Rendered while the M1-backed data loads. */
  loadingFallback?: React.ReactNode;
  children: React.ReactNode;

  /* ---- Presentational-only data for not-yet-backed surfaces (kept as-is) ---- */
  quarantine?: QuarantineItem[];
  channels?: Channel[];
  reference?: ReferenceData;
  fields?: Record<string, FieldDef>;
  access?: Record<string, AccessGrant[]>;
  shareLog?: ShareLogEntry[];
  clients?: string[];
};

export const VaultProvider = ({
  user,
  domains = DEFAULT_DOMAINS,
  domainOrder,
  loadingFallback = null,
  children,
  quarantine = [],
  channels = [],
  reference = EMPTY_REFERENCE,
  fields = {},
  access = {},
  shareLog = [],
  clients = [],
}: VaultProviderProps) => {
  const { message: toast, tone: toastTone, show: showToast } = useTransientToast();
  const granularAuthEnabled = useFeatureFlag(DOCUMENT_VAULT_GRANULAR_AUTH_FLAG, false);
  const hasCapability = useCallback(
    (capability: VaultCapability) =>
      !granularAuthEnabled || (user.capabilities?.includes(capability) ?? false),
    [granularAuthEnabled, user.capabilities],
  );
  const {
    globalLib,
    globalIdByKey,
    loading: attrsLoading,
    saving: attrsSaving,
    addGlobalAttr,
    saveGlobalAttr,
    promoteLocal,
    saveLocal,
  } = useGlobalAttributeAdmin();
  const {
    spaces,
    loading: spacesLoading,
    saving: spacesSaving,
    saveSpace,
    refetch,
  } = useVaultSpaces({ globalLib, globalIdByKey });

  const lookups = useMemo<VaultLookups>(() => {
    const globalKeyByAttributeId = Object.fromEntries(
      Object.entries(globalIdByKey).map(([key, id]) => [id, key]),
    );
    const domainBySpaceId = Object.fromEntries(spaces.map((s) => [s.id, s.domain]));
    const localNameByAttributeId: Record<string, string> = {};
    spaces.forEach((s) => s.locals.forEach((l) => (localNameByAttributeId[l.id] = l.name)));
    const optionValueById = optionValueByIdFromCatalog(
      globalLib,
      spaces.flatMap((s) => s.locals),
    );
    return { domainBySpaceId, globalKeyByAttributeId, localNameByAttributeId, optionValueById };
  }, [globalIdByKey, globalLib, spaces]);

  const value = useMemo<VaultData>(
    () => ({
      user,
      loading: attrsLoading || spacesLoading,
      saving: attrsSaving || spacesSaving,
      spaces,
      globalLib,
      globalIdByKey,
      domains,
      domainOrder,
      documents: NO_SHARED_DOCUMENTS,
      lookups,
      loadingFallback,
      quarantine,
      channels,
      reference,
      fields,
      access,
      shareLog,
      clients,
      saveSpace,
      addGlobalAttr,
      saveGlobalAttr,
      promoteLocal,
      saveLocal,
      // `useVaultSpaces().refetch` returns a Promise; the context contract is
      // fire-and-forget, so discard it to keep `refetch: () => void`.
      refetch: () => void refetch(),
      showToast,
      hasCapability,
    }),
    [
      user,
      hasCapability,
      attrsLoading,
      spacesLoading,
      attrsSaving,
      spacesSaving,
      spaces,
      globalLib,
      globalIdByKey,
      domains,
      domainOrder,
      lookups,
      loadingFallback,
      quarantine,
      channels,
      reference,
      fields,
      access,
      shareLog,
      clients,
      saveSpace,
      addGlobalAttr,
      saveGlobalAttr,
      promoteLocal,
      saveLocal,
      refetch,
      showToast,
    ],
  );

  // Children ALWAYS render, even mid-load. Returning only the fallback here used
  // to unmount `VaultLayout` too, so every navigation that refetched blanked the
  // sidebar. Consumers that compose their own chrome instead of `VaultLayout`
  // should gate on `loading` from `useVaultData()` themselves.
  return (
    <VaultDataContext.Provider value={value}>
      {children}
      <VaultToast message={toast} tone={toastTone} />
    </VaultDataContext.Provider>
  );
};
